import React, { useCallback, useEffect, useMemo, useState } from "react";
import { buildNotasVentasParams, buildRecibosParams, calculateVentasSummaries, processFactura } from "../../print/cuadreUtils";
import { request } from "../../../../utils/request";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { auth } from "../../../../utils/auth";
import { formatCurrency } from "../../../../utils/helpers";

const useData = () => {
    const location = useLocation();
	const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

	const [finishedCallCount, setFinishedCallCount] = useState(0);

	const [ordenes, setOrdenes] = useState([]);
	const [recibos, setRecibos] = useState([]);
    const [notas, setNotas] = useState([]);

	const [summaries, setSummaries] = useState({
		ventas: {
			efectivo: 0,
			transferencia: 0,
			cheque: 0,
			tarjeta: 0,
			'tarjeta-debito': 0,
			deposito: 0,
			otro: 0,
			pagada: 0,
			contado: 0,
			contraEntrega: 0,
			credito: 0,
			facturado: 0,
		},
	});

	const getOrdenes = useCallback(() => {
		const searchString =
			location.search === ""
				? "?paginate=100000"
				: location.search + "&paginate=1000000";
		const hasta = dayjs(queryParams.get("hasta")).add(1, 'days');
		request(`ordenes-compra${searchString}`).then((res) => {
			setOrdenes(res.data.map(orden => processFactura(orden, hasta)));
			setFinishedCallCount((count) => count + 1);
		});
	}, []);

	const getRecibos = useCallback(() => {
		const searchString = new URLSearchParams(buildRecibosParams(queryParams));
		request(`recibos-ingreso?${searchString.toString()}`).then((res) => {
			setRecibos(res.data);
			setFinishedCallCount((count) => count + 1);
		});
	}, []);

	const getNotasCredito = useCallback(() => {
		const searchString = new URLSearchParams(buildNotasVentasParams(queryParams));
		request(`notas-credito?${searchString.toString()}`).then((res) => {
			setNotas(res.data);
			setFinishedCallCount((count) => count + 1);
		});
	}, []);

	const calculateSummaries = useCallback(() => {
		setSummaries({ ventas: calculateVentasSummaries(ordenes, recibos) });
	}, [recibos, ordenes]);

    useEffect(() => {
		getOrdenes();
		getRecibos();
		getNotasCredito();
	}, []);

    useEffect(() => {
		if (finishedCallCount < 3) return;

        calculateSummaries();

        const timeout = setTimeout(() => {
			window.print();
		}, 1000);

		return () => {
			clearTimeout(timeout);
		};
	}, [finishedCallCount]);

	return useMemo(
		() => ({
			ordenes,
			recibos,
			notas,
			summaries,
		}),
		[ordenes, recibos, notas, summaries]
	);
};

const Desktop = ({
    queryParams,
    titulo,
    sucursal,
    vendedorId,
    vendedor,
    summaries,
    ValorTotalNotaCreditos,
    ValorTotalRecibos,
}) => {
    return <div className="d-none d-md-block">
        <div className="row">
            <h2 className="col-md-12 text-center m-b-5">
                {titulo}
            </h2>
            <div className="col-md-12 text-center">
                {sucursal.nombre} - {sucursal.direccion} •{" "}
                {queryParams.get("desde")
                    ? `${dayjs(queryParams.get("desde")).format(
                            "DD/MM/YYYY"
                    )} - `
                    : ""}{" "}
                {dayjs(queryParams.get("hasta")).format("DD/MM/YYYY")}
            </div>

            {vendedorId && (
                <div className="col-md-12 text-center">
                    <strong className="mr-1">Vendedor:</strong> {vendedor?.nombre} {vendedor?.apellido}
                </div>
            )}
        </div>

        <div className="d-flex justify-content-center mt-4">
            <div className="row border" style={{fontSize:"0.9rem", width: "680px"}}>
                <div className="col col-md-12">
                    <h3 className="pt-3 pb-3 text-center border-bottom">Ventas</h3>
                </div>

                <div className="col col-md-7 pb-3">
                    <div className="col col-md-12">
                        <b style={{fontSize: "18px"}}>Total Facturado</b> = <span className="float-right">{formatCurrency(summaries.ventas.facturado)}</span>
                    </div>
                    <div className="col col-md-12 border-top pt-4">
                        <h5 className="my-1 text-center">INGRESOS</h5>
                        <b>Ordenes</b> = <span className="float-right">{formatCurrency(summaries.ventas.pagada)}</span><br/>
                        <b>Recibos</b> = <span className="float-right">{formatCurrency(ValorTotalRecibos, "$")}</span><br/>
                        <div className="border-top"><b>Total Ingresos</b> = <span className="float-right">{formatCurrency(summaries.ventas.pagada + ValorTotalRecibos, "$")}</span></div><br/>
                    </div>
                    <div className="col col-md-12" style={{paddingTop: "1.3rem"}}>
                        <b>Ventas a crédito</b> = <span className="float-right">{formatCurrency(summaries.ventas.credito, "$")}</span><br/>
                        <b>Notas crédito</b> = <span className="float-right">{formatCurrency(ValorTotalNotaCreditos, "$")}</span><br/>
                    </div>
                </div>

                <div className="col col-md-5">
                    <h5 className="py-2 text-center">FORMA DE PAGO</h5>
                    <b>Efectivo</b> = <span className="float-right">{formatCurrency(summaries.ventas.efectivo)}</span><br/>
                    <b>Transferencia</b> = <span className="float-right">{formatCurrency(summaries.ventas.transferencia)}</span><br/>
                    <b>Cheque</b> = <span className="float-right">{formatCurrency(summaries.ventas.cheque)}</span><br/>
                    <b>Tarjeta</b> = <span className="float-right">{formatCurrency(summaries.ventas.tarjeta + summaries.ventas['tarjeta-debito'])}</span><br/>
                    <b>Depósito</b> = <span className="float-right">{formatCurrency(summaries.ventas.deposito)}</span><br/>
                    <b>Otro</b> = <span className="float-right">{formatCurrency(summaries.ventas.otro)}</span><br/>
                </div>

                <div className="col col-md-7 pb-3">
                    <div className="col">
                        <h5 className="py-2 text-center">TÉRMINOS DE PAGO</h5>
                        <b>CONTADO</b> = <span className="float-right">{formatCurrency(summaries.ventas.contado)}</span><br/>
                        <b>CRÉDITO</b> = <span className="float-right">{formatCurrency(summaries.ventas.credito)}</span><br/>
                        <b>CONTRA ENTREGA</b> = <span className="float-right">{formatCurrency(summaries.ventas.contraEntrega)}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
};

const Mobile = ({
    queryParams,
    titulo,
    sucursal,
    vendedorId,
    vendedor,
    summaries,
    ValorTotalNotaCreditos,
    ValorTotalRecibos,
    ordenes,
    recibos,
}) => {
    return <div className="invoice invoice-mobile recibo-print d-md-none">
        <div className="clear-both p-t-30 p-b-10">
            <div>
                <h3 className="sucursal-name text-center">{titulo}</h3>
                <div className="text-center detalles-sucursal">
                    {sucursal.nombre} <br />
                    RNC: {sucursal.rnc} TEL.:{sucursal.telefono}<br />
                    {sucursal.direccion}<br />
                    {queryParams.get("desde")
                        ? `${dayjs(queryParams.get("desde")).format("DD/MM/YYYY")} - ` 
                        : ""}
                    {dayjs(queryParams.get("hasta")).format("DD/MM/YYYY")}
                </div>

                {vendedorId && (
                    <div className="detalles-sucursal text-center">
                        <strong className="mr-1">Vendedor:</strong> {vendedor?.nombre} {vendedor?.apellido}
                    </div>
                )}
            </div>
        </div>

        <div className="mx-2">
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
            <div className="d-block bold tipo-factura text-center uppercase">PEDIDOS</div>
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />

            <div>
                <div className="row producto-item align-items-end" style={{ marginBottom: "5px", marginTop: "5px" }}>
                    <div className="col-6" style={{ lineHeight: "1" }}>{ordenes.length} <strong className="ml-2">Pedidos:</strong></div>
                    <div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.facturado)}</div>
                </div>
            </div>
        </div>

        <div style={{height: '16px'}}></div>
        <div className="mx-2">
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
            <div className="d-block bold tipo-factura text-center uppercase">TÉRMINOS DE PAGO</div>
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
            
            <div className="row producto-item align-items-end" style={{marginTop: "5px", marginBottom: "5px" }}>
                <div className="col-6" style={{ lineHeight: "1" }}><strong>Contado:</strong></div>
                <div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.contado)}</div>
            </div>
            <div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
                <div className="col-6" style={{ lineHeight: "1" }}><strong>Crédito:</strong></div>
                <div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.credito)}</div>
            </div>
            <div className="row producto-item align-items-end">
                <div className="col-6" style={{ lineHeight: "1" }}><strong>Contra Entrega:</strong></div>
                <div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.contraEntrega)}</div>
            </div>
        </div>

        <div style={{height: '16px'}}></div>
        <div className="mx-2">
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
            <div className="d-block bold tipo-factura text-center uppercase">INGRESOS</div>
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
{/* 
            <div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
                <div className="col-6" style={{ lineHeight: "1" }}><strong>Facturas:</strong></div>
                <div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.pagada, "$")}</div>
            </div> */}
            <div className="row producto-item align-items-end" style={{ marginTop: "5px", marginBottom: "5px" }}>
                <div className="col-6" style={{ lineHeight: "1" }}>{recibos.length} <strong className="ml-2">Recibos:</strong></div>
                <div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(ValorTotalRecibos, "$")}</div>
            </div>
            {/* <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
            <div className="row producto-item align-items-end" style={{ marginTop: "5px" }}>
                <div className="col-6" style={{ lineHeight: "1" }}><strong>Total Ingresos:</strong></div>
                <div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.pagada + ValorTotalRecibos, "$")}</div>
            </div>

            <div style={{height: '16px'}}></div>

            <div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
                <div className="col-6" style={{ lineHeight: "1" }}><strong>Ventas a crédito:</strong></div>
                <div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.credito, "$")}</div>
            </div>

            <div className="row producto-item align-items-end">
                <div className="col-6" style={{ lineHeight: "1" }}><strong>Notas crédito:</strong></div>
                <div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(ValorTotalNotaCreditos, "$")}</div>
            </div> */}
        </div>

        <div style={{height: '16px'}}></div>
        <div className="mx-2">
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />
            <div className="d-block bold tipo-factura text-center uppercase">FORMA DE PAGO</div>
            <hr style={{borderStyle: 'dashed', borderWidth: '1px', margin: '0'}} />

            <div className="row producto-item align-items-end" style={{ marginTop: "5px", marginBottom: "5px" }}>
                <div className="col-6" style={{ lineHeight: "1" }}><strong>Efectivo:</strong></div>
                <div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.efectivo)}</div>
            </div>
            <div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
                <div className="col-6" style={{ lineHeight: "1" }}><strong>Transferencia:</strong></div>
                <div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.transferencia)}</div>
            </div>
            <div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
                <div className="col-6" style={{ lineHeight: "1" }}><strong>Cheque:</strong></div>
                <div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.cheque)}</div>
            </div>
            <div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
                <div className="col-6" style={{ lineHeight: "1" }}><strong>Tarjeta:</strong></div>
                <div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.tarjeta + summaries.ventas['tarjeta-debito'])}</div>
            </div>
            <div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
                <div className="col-6" style={{ lineHeight: "1" }}><strong>Depósito:</strong></div>
                <div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.deposito)}</div>
            </div>
            <div className="row producto-item align-items-end" style={{ marginBottom: "5px" }}>
                <div className="col-6" style={{ lineHeight: "1" }}><strong>Otro:</strong></div>
                <div className="col text-right" style={{ lineHeight: "1" }}>{formatCurrency(summaries.ventas.otro)}</div>
            </div>
        </div>
        <div style={{height: '16px'}}></div>
    </div>;
};

const ReporteCuadreCajaVenta = () => {
    const location = useLocation();
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const tipo = dayjs(queryParams.get("tipo"));
    const titulo = tipo === 'cliente' ? 'Reporte Pedidos - Cuadre de Caja resumen' : 'Reporte Ordenes - Cuadre de Caja resumen';

    const user = auth.getUser();
    const sucursal = useMemo(
        () => user.sucursales.find((suc) => suc.id === user.sucursalActual),
        [user]
    );

    const { ordenes, recibos, notas, summaries } = useData();

    const ValorTotalNotaCreditos = useMemo(
        () => notas.reduce((acc, nota) => acc + (nota.estado === "cancelada" ? 0 : parseFloat(nota.total)), 0),
        [notas]
    );
    const ValorTotalRecibos = useMemo(
        () => recibos.reduce((acc, recibo) => acc + (recibo.status === "cancelado" ? 0 : parseFloat(recibo.abono_total)), 0),
        [recibos]
    );

    const vendedorId = queryParams.get("vendedor");
    let vendedor = {};
    if (vendedorId) {
        vendedor = ordenes.find((factura) => factura.vendedor?.id === parseInt(vendedorId))?.vendedor;
    }
    

    return <>
        <style>
            {`
            @import url('https://fonts.googleapis.com/css2?family=Saira+Extra+Condensed:wght@400;700&display=swap');
            @media (max-width: 450px) {
                body {
                    font-family: "Saira Extra Condensed", sans-serif !important;
                    width: 100% !important;
                    font-size: 12pt !important;
                }

                @media print {
                    body {
                        min-width: auto !important;
                    }
                }

                @page {
                    size: 80mm auto;
                    margin: 0;
                }

                @page {
                    size: 80mm 300mm !important;
                    margin-top: 0 !important;
                    margin-bottom: 0 !important;
                    margin-left: 20px !important;
                    margin-right: 20px !important;
                }

                .invoice {
                    width: 100% !important;
                    padding: 0 20px 0 5px;
                }
                .invoice small {
                    font-size: 10pt;
                    line-height: 1;
                }
                .invoice-table {
                    display: none;
                }
                .sm-invoice-table {
                    display: block;
                }
                .invoice h3 {
                    margin-bottom: 0.1rem;
                }
                .invoice h3.signature-title {
                    font-weight: 600 !important;
                }
                .client-info-title {
                    font-weight: 600 !important;
                }
                .info-cliente-responsive .info-client__response {
                    line-height: 1.2 !important;
                }
                .invoice-name {
                    font-weight: 600 !important;
                }
                .info-cliente-responsive {
                    line-height: 1.2 !important;
                    margin-bottom: 5px !important;
                }
                .info-cliente-responsive .row{
                    margin-bottom: 6px;
                }
                .invoice-mobile {
                    display: block;
                    width: 100% !important;
                }
                hr {
                    margin: 5px 0;
                }
                .sucursal-name {
                    font-size: 14pt;
                }
                .detalles-sucursal {
                    line-height: 1;
                    margin-bottom: 10px;
                }
                .top-info {
                    line-height: 1;
                }
                .sucursal-name-sm {
                    display:block;
                }
                .producto-item {
                    font-size: 10pt;
                }
                .factura-logo{
                    display:none;
                }
                .sucursal-name-lg{
                    display:none;
                }
                .info-cliente-responsive .row {
                    margin-bottom: 0 !important;
                }
                .client-info-title {
                    min-width: 90px;
                }
                .tabla-titulos small {
                    font-weight: 600;
                }
                .totales {
                    line-height: 1.2;
                    margin-bottom: 10px;
                }
                .summary-text{
                    // max-width:39% !important;
                    width:auto !important;
                    flex:auto;
                    padding-left: 5px !important;
                    padding-right: 2px !important;
                }
                .justify-content-between {
                    justify-content: flex-end !important;
                }
                .justify-content-between :nth-child(2){
                    max-width: 33% !important;
                }
            }`}
        </style>
            
        <Desktop
            queryParams={queryParams}
            titulo={titulo}
            sucursal={sucursal}
            vendedorId={vendedorId}
            vendedor={vendedor}
            ValorTotalNotaCreditos={ValorTotalNotaCreditos}
            ValorTotalRecibos={ValorTotalRecibos}
            summaries={summaries}
        />

        <Mobile
            queryParams={queryParams}
            titulo={titulo}
            sucursal={sucursal}
            vendedorId={vendedorId}
            vendedor={vendedor}
            ValorTotalNotaCreditos={ValorTotalNotaCreditos}
            ValorTotalRecibos={ValorTotalRecibos}
            summaries={summaries}
            ordenes={ordenes}
            recibos={recibos}
        />
    </>;
};

export default ReporteCuadreCajaVenta;
